import React, { useState, useEffect } from 'react';
import { ShieldAlert, RefreshCw, Power, Search, Activity } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { FormBox } from '../../components/ui/FormBox';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import { voipblApi, VoipblSettings } from './voipblApi';
import { Button } from '../../components/ui/button';
import { Toggle } from '../../components/ui/Toggle';
import { cn } from '../../lib/utils';
import Modal from '../../components/ui/Modal';

export default function VoipblPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [settings, setSettings] = useState<VoipblSettings>({
    enabled: false,
    autoUpdate: true,
  });
  const [stats, setStats] = useState<{ packetsDropped: number; bytesDropped: number } | null>(null);
  const [searchIpQuery, setSearchIpQuery] = useState('');
  const [searchResult, setSearchResult] = useState<{ ip: string; isBlocked: boolean } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const loadSettings = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      const res = await voipblApi.getSettings();
      setSettings(res.data);
      try {
        const statsRes = await voipblApi.getStats();
        setStats(statsRes.data);
      } catch (e) {
        console.error('Failed to load stats', e);
      }
    } catch (error) {
      console.error(error);
      showError(t('voipbl.errors.loadFailed'));
    } finally {
      if (showLoader) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleToggleEnable = async (enabled: boolean) => {
    try {
      setSaving(true);
      const res = await voipblApi.updateSettings({ enabled });
      setSettings(res.data);
      showSuccess(enabled ? t('voipbl.messages.enabled') : t('voipbl.messages.disabled'));
    } catch (error) {
      showError(t('voipbl.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoUpdate = async (autoUpdate: boolean) => {
    try {
      setSaving(true);
      const res = await voipblApi.updateSettings({ autoUpdate });
      setSettings(res.data);
      showSuccess(t('voipbl.messages.autoUpdateSaved'));
    } catch (error) {
      showError(t('voipbl.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const res = await voipblApi.triggerSync();
      if (res.data.success) {
        showSuccess(t('voipbl.messages.syncSuccess'));
        setSyncResult(res.data.stats || {});
        await loadSettings(false);
      } else {
        showError(res.data.output || t('voipbl.errors.syncFailed'));
        setSyncing(false);
      }
    } catch (error) {
      showError(t('voipbl.errors.syncFailed'));
      setSyncing(false);
    }
  };

  const closeSyncModal = () => {
    setSyncing(false);
    setSyncResult(null);
  };

  const handleSearchIp = async () => {
    if (!searchIpQuery.trim()) return;
    setIsSearching(true);
    setSearchResult(null);
    try {
      const { data } = await voipblApi.searchIp(searchIpQuery.trim());
      setSearchResult(data);
    } catch (error) {
      showError(t('voipbl.search.invalidIp') || 'Invalid IP');
    } finally {
      setIsSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <ModuleFormHeader
          icon={<ShieldAlert className="h-5 w-5 text-primary" />}
          title={t('voipbl.title')}
          hideBackButton={true}
          onBack={() => {}}
          backButtonText=""
        />
        <div className="mt-8 flex justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col" style={{ height: 'calc(100vh - 80px)' }}>
      <ModuleFormHeader 
        icon={<ShieldAlert className="h-5 w-5 text-primary" />}
        title={t('voipbl.title')}
        hideBackButton={true}
        onBack={() => {}}
        backButtonText=""
        actions={[
          {
            icon: <RefreshCw className={cn("h-5 w-5", isRefreshing ? "animate-spin" : "")} />,
            tooltip: t('common.refresh') || 'Refresh',
            onClick: () => loadSettings(false),
            disabled: loading || isRefreshing,
          },
          {
            icon: <Search className="h-5 w-5" />,
            tooltip: t('voipbl.search.title'),
            onClick: () => setIsSearchModalOpen(true),
          }
        ]}
      />

      <div className="mt-8 flex-1 overflow-y-auto pb-24 px-1">
        <div className="w-full space-y-6">
          <FormBox 
            title={t('voipbl.title')}
            icon={<ShieldAlert className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Status Panel */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${settings.enabled ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                    <Power className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      {t('voipbl.form.status.title')}
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider border", 
                        settings.enabled ? "bg-green-500/10 text-green-600 border-green-500/20" : "bg-slate-500/10 text-slate-600 border-slate-500/20"
                      )}>
                        {settings.enabled ? t('voipbl.status.active') : t('voipbl.status.inactive')}
                      </span>
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {settings.enabled 
                        ? t('voipbl.status.activeDesc') 
                        : t('voipbl.status.inactiveDesc')}
                    </p>
                  </div>
                </div>
                
                <Toggle
                  value={settings.enabled}
                  onChange={(checked) => handleToggleEnable(checked)}
                  disabled={saving}
                />
              </div>

              {/* Auto Update Panel */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${settings.autoUpdate ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                    <RefreshCw className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      {t('voipbl.form.autoUpdate.title')}
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider border", 
                        settings.autoUpdate ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" : "bg-slate-500/10 text-slate-600 border-slate-500/20"
                      )}>
                        {settings.autoUpdate ? 'AUTO' : 'MANUAL'}
                      </span>
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {settings.autoUpdate 
                        ? t('voipbl.form.autoUpdate.activeDesc') 
                        : t('voipbl.form.autoUpdate.inactiveDesc')}
                    </p>
                  </div>
                </div>
                
                <Toggle
                  value={settings.autoUpdate || false}
                  onChange={(checked) => handleToggleAutoUpdate(checked)}
                  disabled={saving}
                />
              </div>
            </div>
          </FormBox>

          <FormBox 
            title={t('voipbl.form.sync.title')}
            icon={<RefreshCw className="h-5 w-5" />}
          >
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                    {t('voipbl.labels.lastSync')}
                  </div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                    {settings.lastSyncAt ? new Date(settings.lastSyncAt).toLocaleString() : t('common.never')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                    {t('voipbl.labels.status')}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${
                      settings.syncStatus === 'success' ? 'bg-green-500' : 
                      settings.syncStatus === 'failed' ? 'bg-red-500' :
                      settings.syncStatus === 'syncing' ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'
                    }`} />
                    <span className="text-sm font-medium text-slate-900 dark:text-white capitalize leading-none">
                      {settings.syncStatus || 'Idle'}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                    {t('voipbl.labels.totalBanned')}
                  </div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400 leading-none">
                    {settings.ipCount ? settings.ipCount.toLocaleString() : '0'}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">IP Addresses</div>
                </div>
              </div>
            </div>
          </FormBox>

          <FormBox 
            title={t('voipbl.search.statsTitle')}
            icon={<Activity className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-4">
                <div className="p-3 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                    {t('voipbl.search.packetsDropped')}
                  </div>
                  <div className="text-xl font-bold text-slate-900 dark:text-white leading-none mt-1">
                    {stats ? stats.packetsDropped.toLocaleString() : '0'}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-4">
                <div className="p-3 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                  <Activity className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                    {t('voipbl.search.bytesDropped')}
                  </div>
                  <div className="text-xl font-bold text-slate-900 dark:text-white leading-none mt-1">
                    {stats ? (() => {
                      const b = stats.bytesDropped;
                      if (b < 1024) return `${b} B`;
                      if (b < 1024 * 1024) return `${(b / 1024).toFixed(2)} KB`;
                      return `${(b / (1024 * 1024)).toFixed(2)} MB`;
                    })() : '0 B'}
                  </div>
                </div>
              </div>
            </div>
          </FormBox>
        </div>
      </div>

      <Modal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        title={t('voipbl.search.title')}
        size="md"
        showCloseButton={true}
      >
        <div className="py-4 space-y-6">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md leading-5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                placeholder={t('voipbl.search.placeholder')}
                value={searchIpQuery}
                onChange={(e) => setSearchIpQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchIp();
                }}
                autoFocus
              />
            </div>
            <Button 
              onClick={handleSearchIp} 
              disabled={isSearching || !searchIpQuery.trim()}
              className="bg-primary text-white hover:bg-primary/90"
            >
              {isSearching ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t('voipbl.search.button')}
            </Button>
          </div>

          {searchResult && (
            <div className={`p-4 rounded-lg flex items-start gap-3 ${
              searchResult.isBlocked 
                ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800/30' 
                : 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-800/30'
            }`}>
              <ShieldAlert className="h-6 w-6 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-base font-semibold">
                  {searchResult.ip}
                </h4>
                <p className="text-sm mt-1">
                  {searchResult.isBlocked ? t('voipbl.search.blocked') : t('voipbl.search.notBlocked')}
                </p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <FixedActionBar
        showCancel={false}
        saveDisabled={syncing || !settings.enabled}
        onSave={handleManualSync}
        saveText={syncing ? t('common.syncing') : t('voipbl.actions.syncNow')}
        isLoading={syncing}
      />

      <Modal 
        isOpen={syncing} 
        onClose={syncResult ? closeSyncModal : () => {}} 
        title={t('voipbl.actions.syncing')} 
        size="md" 
        showCloseButton={!!syncResult}
      >
        <div className="flex flex-col py-6 space-y-4">
          {!syncResult ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <RefreshCw className="h-12 w-12 text-primary animate-spin" />
              <p className="text-lg font-medium text-slate-800 dark:text-slate-200">
                {t('voipbl.messages.syncInProgress')}
              </p>
              <p className="text-sm text-slate-500 text-center">
                {t('voipbl.messages.syncWarning')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col space-y-4 px-4">
              <div className="flex items-center justify-center mb-2">
                <div className="h-16 w-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <ShieldAlert className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-center text-slate-900 dark:text-white">
                {t('voipbl.messages.syncComplete')}
              </h3>
              
              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700 mt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">{t('voipbl.labels.previousCount')}:</span>
                  <span className="font-semibold">{syncResult.previous?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">{t('voipbl.labels.newCount')}:</span>
                  <span className="font-semibold text-primary">{syncResult.current?.toLocaleString() || 0}</span>
                </div>
                <div className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">{t('voipbl.labels.difference')}:</span>
                  <span className={`font-bold ${syncResult.diff > 0 ? 'text-green-600 dark:text-green-400' : syncResult.diff < 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'}`}>
                    {syncResult.diff > 0 ? '+' : ''}{syncResult.diff?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button onClick={closeSyncModal} variant="default" className="w-full sm:w-auto">
                  {t('common.ok')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
